import React, { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/utils';

interface PreloaderProps {
  progress: number;
  status: string;
  isComplete: boolean;
  onComplete?: () => void;
}

export const Preloader: React.FC<PreloaderProps> = ({
  progress,
  status,
  isComplete,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!isComplete) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 150);

    return () => clearTimeout(timer);
  }, [isComplete, onComplete]);

  if (!isVisible) return null;

  const isAnimatingOut = isComplete;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white transition-opacity duration-150',
        isAnimatingOut ? 'opacity-0' : 'opacity-100'
      )}
    >
      {/* Logo */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-blue md:text-6xl">
          Krasota
        </h1>
        <p className="mt-2 text-lg text-blue-70">
          Коммерческая недвижимость
        </p>
      </div>

      {/* Progress */}
      <div className="w-80 max-w-md">
        <div className="mb-4 flex justify-between text-sm text-blue-70">
          <span>{status}</span>
          <span>{Math.round(progress)}%</span>
        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-light-blue">
          <div
            className="h-full bg-gradient-to-r from-blue to-blue-70"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Dots */}
      <div className="mt-8 flex space-x-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-3 w-3 rounded-full bg-blue"
          />
        ))}
      </div>

      {/* Status text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-blue-70">
          {progress < 30 && 'Загружаем критические ресурсы...'}
          {progress >= 30 && progress < 60 && 'Получаем данные с сервера...'}
          {progress >= 60 && progress < 90 && 'Подготавливаем интерфейс...'}
          {progress >= 90 && progress < 100 && 'Почти готово...'}
          {progress >= 100 && 'Готово!'}
        </p>
      </div>
    </div>
  );
};
